import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  usePropertyField,
  useContentToken,
  EditableToken,
  useSlotGroup,
  EditableSlotGroup,
} from '@vibesites/runtime/react';

const contactLinkDef: SlotGroupDefinition = {
  label: 'Contact Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function AccessibilityStatement() {
  const heading = useContentToken('accessibilitystatement.heading');
  const domain = usePropertyField('property_domain') as string || '';

  const iframeSrc = domain
    ? `https://portal.audioeye.com/accessibility-statement.html?domain=${encodeURIComponent(domain)}`
    : '';

  const contactLink = useSlotGroup('accessibilitystatement.contact_link', contactLinkDef);

  return (
    <section className="bg-white py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="accessibilitystatement.heading"
          as="h1"
          className="sr-only"
        >
          {heading}
        </EditableToken>

        {iframeSrc && (
          <iframe
            src={iframeSrc}
            title="Accessibility Statement"
            className="w-full border-0"
            style={{ height: '2300px' }}
          />
        )}

        <EditableSlotGroup
          groupPath="accessibilitystatement.contact_link"
          definition={contactLinkDef}
        >
          {contactLink.url && (
            <div className="mt-8">
              <a
                href={contactLink.url}
                target={contactLink._target || '_self'}
                className="font-body text-primary underline"
              >
                {contactLink.label || 'Contact Us'}
              </a>
            </div>
          )}
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AccessibilityStatement',
  displayName: 'Accessibility Statement',
  tokens: {
    'accessibilitystatement.heading': {
      label: 'Page Heading',
      type: 'text',
      defaultValue: 'Accessibility Statement',
    },
  },
  slotGroups: {
    'accessibilitystatement.contact_link': contactLinkDef,
  },
};

(AccessibilityStatement as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AccessibilityStatement;
